import rtm, { AuthenticationFailedError } from '@pearsontechnology/aero-rtm';
import appConfig from 'appConfig';
import OverlayManager from 'OverlayManager';
import assetProvider from 'services/assets/AssetProvider';
import { removeToken } from 'ConnectionManager/sessionManager';
import { logNewrelicAddPageAction } from '../cookies/NewRelic';
import messages from './messages';
import * as constants from '../services/coursewareItem/constants';

class ConnectionManager {
  _getIntlErrorMessage(error) {
    let errorMessage;
    switch (error.constructor) {
      case AuthenticationFailedError:
        errorMessage = messages.credentialsError;
        break;
      default:
        return error.message;
    }
    return errorMessage;
  }

  registerServices = (connection) => {
    assetProvider.register(connection);
  };

  /**
   * * Monitors WS connection activity
   */
  _monitorConnection() {
    const { piSession } = window;
    const piUserId = piSession.userId();
    const debugRequest = [
      constants.RTM_LEARNER_EVALUATE,
      constants.RTM_LEARNER_STUDENT_SCOPE_SET,
    ];
    const shouldDebug = (type) => debugRequest.includes(type) || !!debugRequest.find((debugType) => type.startsWith(debugType));
    // setting up listeners for onclose and onOpen events
    // show connect/disconnect overlay
    this.connection.onClose((payload) => {
      const eventName = 'websocketClosedConnection';
      const eventData = { piUserId, description: 'Closed websocket connection' };
      logNewrelicAddPageAction(eventName, eventData);

      if (!payload.disableReconnect) {
        OverlayManager.notify({ showOverlay: true });
      }
    });
    this.connection.onOpen(() => {
      OverlayManager.notify({ showOverlay: false });

      // NOTE - this "onOpen" callback ONLY gets run when the socket is closed and reopened
      // The initial opening of the webocket doesn't trigger this "onOpen"
      const eventName = 'websocketReopenConnection';
      const eventData = { piUserId, description: 'Reopened websocket connection' };
      logNewrelicAddPageAction(eventName, eventData);
    });
    this.connection.onHeartBeatFail((e) => {
      const eventName = 'websocketHeartBeatFailed';
      const eventData = { piUserId, error: e, description: 'Websocket heart beat failed' };
      logNewrelicAddPageAction(eventName, eventData);
    });

    this.connection.onRequest((request) => {
      if (!shouldDebug(request.type)) return;
      const eventName = 'websocketRequest';
      const { clientId } = this.connection;
      const eventData = { ...request, clientId, piUserId };
      logNewrelicAddPageAction(eventName, eventData);
    });
    this.connection.onResponse((response, responseTime) => {
      if (!shouldDebug(response.type)) return;
      const eventName = 'websocketResponse';
      const { clientId } = this.connection;
      const eventData = { ...response, clientId, piUserId, responseTime };
      logNewrelicAddPageAction(eventName, eventData);
    });

    // monitor browser connectivity
    // detect if the navigator is offline
    window.addEventListener('offline', () => this.connection.reconnect());
  }

  async openConnection() {
    return new Promise(async (resolve, reject) => {
      if (this.hasCredentials()) {
        try {
          this.connection = await rtm.open(appConfig.getLearnerWebsocketURL(), this.credentials);
          this.registerServices(this.connection);
          this._monitorConnection();
          resolve();
        } catch (error) {
          error.message = this._getIntlErrorMessage(error);
          reject(error);
        }
      } else {
        const error = new AuthenticationFailedError();
        error.message = this._getIntlErrorMessage(error);
        reject(error);
      }
    });
  }

  getConnection() {
    return this.connection;
  }

  closeConnection() {
    this.connection.shutDown();
    this.connection = undefined;
  }

  hasCredentials() {
    return !!this.credentials;
  }

  setCredentials(credentials) {
    this.credentials = credentials;
  }

  deleteCredentials = () => {
    this.credentials = undefined;
    removeToken();

    if (this.connection) {
      this.closeConnection();
    }
    if (this.logoutCallback) {
      this.logoutCallback();
    }
  }

  setLogoutCallback(logoutCallback) {
    this.logoutCallback = logoutCallback;
  }
}

export default new ConnectionManager();
